/**
 * NewPolicyPage — /agents/libraries/model-runtime-policies/new[?from=<policy id>]
 * Creates a runtime policy on a FormPage (never inline on the list). `from` starts the form from a copy of an
 * existing policy (the detail page's Duplicate): same settings, a new name and slug, saved as a draft.
 * Template example: NextAdmin `profile/account/page.tsx` and demo `/form-layouts`.
 */
import type React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  createModelRuntimePolicyMutation,
  getModelRuntimePolicyOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import { Form, FormActions, useZodForm } from '../../../components/form/index.js';
import { FormPage } from '../../../components/page/FormPage.js';
import { Card, QueryBoundary, useToast } from '../../../components/ui/index.js';
import { LimitsSection, PermissionsSection, ProviderSection } from './PolicyEditorSections.js';
import { IdentitySection, RoutingSection } from './PolicyEditorSectionsA.js';
import { PolicyModelsSection } from './PolicyModelsSection.js';
import { policyPath } from './PolicyCatalogList.js';
import { POLICIES_PATH, asApiError, useRefreshPolicies } from './policy-api.js';
import {
  type PolicyFormValues,
  defaultPolicyValues,
  formValuesToPayload,
  policyFormSchema,
  policyToFormValues,
} from './types.js';

function PolicyCreateForm({ initial }: { initial: PolicyFormValues }) {
  const navigate = useNavigate();
  const { notify } = useToast();
  const refresh = useRefreshPolicies();
  const create = useMutation(createModelRuntimePolicyMutation());
  const form = useZodForm(policyFormSchema, { defaultValues: initial });

  const onSubmit = async (values: Parameters<typeof formValuesToPayload>[0]) => {
    const created = await create.mutateAsync({ body: formValuesToPayload(values) }).catch((error: unknown) => {
      throw asApiError(error);
    });
    await refresh();
    notify('Policy created');
    navigate(policyPath(created.data.id));
  };

  const sections = [
    <IdentitySection key="identity" isCreating canEdit />,
    <PolicyModelsSection key="models" isCreating canEdit policyId={null} />,
    <RoutingSection key="routing" isCreating canEdit />,
    <LimitsSection key="limits" isCreating canEdit />,
    <PermissionsSection key="permissions" isCreating canEdit />,
    <ProviderSection key="provider" isCreating canEdit />,
  ];

  return (
    <Form form={form} onSubmit={onSubmit} guardUnsaved>
      {sections.map((section, index) => (
        <div
          key={section.key}
          className={index === 0 ? undefined : 'flex flex-col gap-8 border-t border-card-border pt-8'}
        >
          {section}
        </div>
      ))}
      <FormActions submitLabel="Create policy" cancelTo={POLICIES_PATH} />
    </Form>
  );
}

export const NewPolicyPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const fromId = Number(searchParams.get('from'));
  const copying = Number.isInteger(fromId) && fromId > 0;
  const source = useQuery({ ...getModelRuntimePolicyOptions({ path: { id: fromId } }), enabled: copying });

  return (
    <FormPage
      title="New runtime policy"
      subtitle="An ordered list of models plus the limits a run must stay within."
      breadcrumbs={[
        { label: 'Libraries', to: '/agents/libraries' },
        { label: 'Runtime policies', to: POLICIES_PATH },
      ]}
      backTo={POLICIES_PATH}
      backLabel="Runtime policies"
      aside={
        <Card title="How a policy is used">
          <ol className="list-decimal space-y-2 pl-5 text-sm text-body">
            <li>People pick a policy, never a model. The policy decides which models run, in order.</li>
            <li>
              Each kind of work has a default policy and a list of allowed ones, set under{' '}
              <Link to={POLICIES_PATH} className="text-accent-text hover:underline">
                Kinds of work
              </Link>
              .
            </li>
            <li>Archiving a policy moves everything that used it to the default for its kind of work.</li>
          </ol>
        </Card>
      }
    >
      {copying ? (
        <QueryBoundary query={source} label="the policy to copy">
          {(response) => {
            const base = policyToFormValues(response.data);
            return (
              <PolicyCreateForm
                initial={{ ...base, name: `Copy of ${base.name}`, slug: `${base.slug}-copy`, status: 'draft' }}
              />
            );
          }}
        </QueryBoundary>
      ) : (
        <PolicyCreateForm initial={defaultPolicyValues()} />
      )}
    </FormPage>
  );
};
